import { useMutation, useQuery, useQueryClient } from 'react-query';
import { API_AUTH_TYPES, API_ROUTES, ApiRequest } from '../api';
import { useEffect } from 'react';

const bunmailApi = new ApiRequest(API_ROUTES.BUNMAIL, API_AUTH_TYPES.ADVISOR);
const smartTableApi = new ApiRequest(API_ROUTES.SMART_TABLE, API_AUTH_TYPES.ADVISOR);

// All smart tables that should be prefetched for instant loading
const smartTablesToPreload = [
    'client_meetings',
    'households',
    'users',
    'prospects',
    'onboarding',
];

// Commonly used email folders/labels to prefetch
const emailFoldersToPreload = [
    { label: 'INBOX', queryLabel: 'inbox' },
    { label: 'SENT', queryLabel: 'sent' },
    { label: 'DRAFT', queryLabel: 'draft' },
    { label: 'STARRED', queryLabel: 'starred' },
];

const useAdvisorPreloadedData = (parentDataLoaded) => {
    const queryClient = useQueryClient();

    // Prefetch all labels/folders with automatic background refetch
    const { data: labelsData } = useQuery({
        queryFn: bunmailApi.getFn({
            endpoint: 'index_labels',
        }),
        queryKey: ['labels'],
        enabled: parentDataLoaded,
        refetchOnWindowFocus: false,
        refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes since labels don't change often
        refetchIntervalInBackground: true,
        staleTime: 4 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
    });

    // Prefetch label counts for instant sidebar rendering - run immediately
    useQuery({
        queryFn: bunmailApi.getFn({
            endpoint: 'label_counts',
        }),
        queryKey: ['label-counts'],
        enabled: parentDataLoaded,
        refetchOnWindowFocus: false,
        refetchInterval: 2 * 60 * 1000, // Refresh every 2 minutes to keep counts fresh
        refetchIntervalInBackground: true,
        staleTime: 90 * 1000, // 90 seconds
        cacheTime: 10 * 60 * 1000, // Keep in cache longer
        // Ensure data is immediately available
        refetchOnMount: 'always',
        // Keep background refetching active
        refetchIntervalInBackground: true,
    });

    // Prefetch email threads for all labels (system and custom) once labels are loaded
    /*
    useEffect(() => {
        if (!labelsData || !parentDataLoaded) return;

        // Helper function to prefetch a label
        const prefetchLabel = async (labelId, labelName) => {
            // Prefetch first page
            await queryClient.prefetchQuery({
                queryKey: ['index_threads', labelId.toLowerCase(), '1', false],
                queryFn: bunmailApi.getFn({
                    endpoint: 'index_threads',
                    params: ['1', false],
                    queryParams: {
                        label: labelId.toLowerCase()
                    }
                }),
                staleTime: 15 * 60 * 1000,
                cacheTime: 30 * 60 * 1000,
            });

            // For high-traffic labels, also prefetch second page
            if (['INBOX', 'SENT'].includes(labelId.toUpperCase())) {
                await queryClient.prefetchQuery({
                    queryKey: ['index_threads', labelId.toLowerCase(), '2', false],
                    queryFn: bunmailApi.getFn({
                        endpoint: 'index_threads',
                        params: ['2', false],
                        queryParams: {
                            label: labelId.toLowerCase()
                        }
                    }),
                    staleTime: 15 * 60 * 1000,
                    cacheTime: 30 * 60 * 1000,
                });
            }
        };

        // Prefetch system labels
        if (labelsData.visible_system_labels) {
            labelsData.visible_system_labels.forEach(label => {
                if (label.id) {
                    prefetchLabel(label.id, label.name);
                }
            });
        }

        // Prefetch user/custom labels
        if (labelsData.user_labels) {
            const processUserLabels = (labels) => {
                labels.forEach(label => {
                    if (label.id && !label.isVirtualParent) {
                        // Only prefetch real labels, not virtual parent folders
                        prefetchLabel(label.id, label.name);
                    }
                    // Process children recursively
                    if (label.children && label.children.length > 0) {
                        processUserLabels(label.children);
                    }
                });
            };
            processUserLabels(labelsData.user_labels);
        }

        // Also prefetch some common system labels that might not be in visible_system_labels
        const commonLabels = ['INBOX', 'SENT', 'DRAFT', 'STARRED', 'SPAM', 'TRASH'];
        commonLabels.forEach(labelId => {
            prefetchLabel(labelId, labelId);
        });

    }, [labelsData, parentDataLoaded, queryClient]);
    */

    // OPTIMIZATION: Prefetch all smart tables using React Query's native refetchInterval
    // This makes all smart table navigation instant after login with automatic background updates

    // Client meetings smart table
    useQuery({
        queryKey: ["smart-table-complete-data", { tableId: 'client_meetings' }],
        queryFn: async () => {
            console.log(`📊 Fetching smart table: client_meetings`);
            const response = await smartTableApi.get({
                endpoint: "table",
                params: ['client_meetings', "default", 0, true],
            });
            console.log(`✅ Fetched client_meetings: ${response.rows?.length} rows, ${response.metadata?.all_views?.length} views`);
            return response;
        },
        enabled: parentDataLoaded,
        refetchOnWindowFocus: false,
        refetchInterval: 20 * 60 * 1000,
        refetchIntervalInBackground: true,
        staleTime: 25 * 60 * 1000,
        cacheTime: 30 * 60 * 1000,
        retry: 3,
    });

    // Households smart table
    useQuery({
        queryKey: ["smart-table-complete-data", { tableId: 'households' }],
        queryFn: async () => {
            console.log(`📊 Fetching smart table: households`);
            const response = await smartTableApi.get({
                endpoint: "table",
                params: ['households', "default", 0, true],
            });
            console.log(`✅ Fetched households: ${response.rows?.length} rows, ${response.metadata?.all_views?.length} views`);
            return response;
        },
        enabled: parentDataLoaded,
        refetchOnWindowFocus: false,
        refetchInterval: 20 * 60 * 1000,
        refetchIntervalInBackground: true,
        staleTime: 25 * 60 * 1000,
        cacheTime: 30 * 60 * 1000,
        retry: 3,
    });

    // Users smart table
    useQuery({
        queryKey: ["smart-table-complete-data", { tableId: 'users' }],
        queryFn: async () => {
            console.log(`📊 Fetching smart table: users`);
            const response = await smartTableApi.get({
                endpoint: "table",
                params: ['users', "default", 0, true],
            });
            console.log(`✅ Fetched users: ${response.rows?.length} rows, ${response.metadata?.all_views?.length} views`);
            return response;
        },
        enabled: parentDataLoaded,
        refetchOnWindowFocus: false,
        refetchInterval: 20 * 60 * 1000,
        refetchIntervalInBackground: true,
        staleTime: 25 * 60 * 1000,
        cacheTime: 30 * 60 * 1000,
        retry: 3,
    });

    // Prospects smart table
    useQuery({
        queryKey: ["smart-table-complete-data", { tableId: 'prospects' }],
        queryFn: async () => {
            console.log(`📊 Fetching smart table: prospects`);
            const response = await smartTableApi.get({
                endpoint: "table",
                params: ['prospects', "default", 0, true],
            });
            console.log(`✅ Fetched prospects: ${response.rows?.length} rows, ${response.metadata?.all_views?.length} views`);
            return response;
        },
        enabled: parentDataLoaded,
        refetchOnWindowFocus: false,
        refetchInterval: 20 * 60 * 1000,
        refetchIntervalInBackground: true,
        staleTime: 25 * 60 * 1000,
        cacheTime: 30 * 60 * 1000,
        retry: 3,
    });

    // Onboarding smart table
    useQuery({
        queryKey: ["smart-table-complete-data", { tableId: 'onboarding' }],
        queryFn: async () => {
            console.log(`📊 Fetching smart table: onboarding`);
            const response = await smartTableApi.get({
                endpoint: "table",
                params: ['onboarding', "default", 0, true],
            });
            console.log(`✅ Fetched onboarding: ${response.rows?.length} rows, ${response.metadata?.all_views?.length} views`);
            return response;
        },
        enabled: parentDataLoaded,
        refetchOnWindowFocus: false,
        refetchInterval: 20 * 60 * 1000,
        refetchIntervalInBackground: true,
        staleTime: 25 * 60 * 1000,
        cacheTime: 30 * 60 * 1000,
        retry: 3,
    });
};

export default useAdvisorPreloadedData;
